import {createEntityAdapter, createSlice, EntityId, PayloadAction} from '@reduxjs/toolkit';
import {mapValues} from 'lodash';
import {
  AnswerData,
  MDQMeta,
  MedicalQuestionnaireKey,
  MethodKey,
  QuestionnaireEntity,
} from '~/types/models/questionnaire';
import {RootState} from '~/redux/root-reducer';
import {
  addQuestionnaire,
  deleteQuestionnaire,
  fetchQuestionnaires,
  submitOrLogMdq,
  updateQuestionnaire,
} from '~/redux/questionnaires/thunk';
import {
  ReducerState,
  addIdsToSources,
  createSelectAllBySource,
  deleteSuccess,
  fetchSuccess,
  upsertMany,
  upsertManyMutably,
  DEFAULT_SOURCE_REDUCER_STATE,
  upsertOneMutably,
} from 'redux-thunk-kit';
import {SOURCES_QUESTIONNAIRE} from '~/redux/questionnaires/entity-config';
import {getObjNthItem} from '~/utils/helpers';
import {METHOD_KEYS} from '~/constants';

interface InitialState extends ReducerState {
  methodKey: MethodKey;
  medicalUUID?: string | null;
  activeQuestion: {index: number; class: string; keys: string[]};
  medicalQuestionnaire: QuestionnaireEntity;
  collectedData: Record<MedicalQuestionnaireKey, AnswerData>;
  answerOrder: number;
  userLocalPhotoURL: string;
  questionnaireMeta: MDQMeta;
}

const sources = mapValues(SOURCES_QUESTIONNAIRE, () => DEFAULT_SOURCE_REDUCER_STATE);

const questionnairesAdapter = createEntityAdapter<QuestionnaireEntity>();

const initialState = questionnairesAdapter.getInitialState<InitialState>({
  sources,
  methodKey: METHOD_KEYS.WEIGHT_LOSS_PROGRAM,
  medicalUUID: null,
  activeQuestion: {
    index: 0,
    class: '',
    keys: ['START'],
  },
  medicalQuestionnaire: {} as QuestionnaireEntity,
  collectedData: {} as Record<MedicalQuestionnaireKey, AnswerData>,
  answerOrder: 0,
  userLocalPhotoURL: '',
  questionnaireMeta: {} as MDQMeta,
});

const questionnaires = createSlice({
  name: 'questionnaires',
  initialState,
  reducers: {
    setActiveQuestionnaire(state, {payload}: PayloadAction<{value: any}>) {
      const {value} = payload;
      state.activeQuestion = value;
    },
    setNextQuestionnaire(state, {payload}: PayloadAction<{value: MedicalQuestionnaireKey}>) {
      const {value} = payload;
      state.activeQuestion.keys.push(value);
    },
    setBackQuestionnaire(state, {payload}: PayloadAction<{key?: MedicalQuestionnaireKey}>) {
      const {key} = payload;
      if (key) {
        const index = state.activeQuestion.keys.findIndex(val => val === key);
        state.activeQuestion.keys = state.activeQuestion.keys.slice(0, index + 1);
      } else {
        state.activeQuestion.keys.pop();
      }
    },
    setQuestionnaireAnswer(
      state,
      {
        payload,
      }: PayloadAction<{
        key: MedicalQuestionnaireKey;
        index: string;
        data: any;
        order: number;
        questions?: Array<Record<any, any>>;
      }>,
    ) {
      const {key, data, index, questions, order} = payload;
      if (!state.collectedData[key]) {
        state.collectedData[key] = {} as AnswerData;
      }
      if (!!questions && questions.length > 0) {
        state.collectedData[key].questions = questions;
      }
      state.collectedData[key].data = data;
      state.collectedData[key].index = index;
      state.collectedData[key].order = order;
      state.answerOrder = order;
    },
    setMethodKey(state, {payload}: PayloadAction<{key: MethodKey}>) {
      const {key} = payload;
      state.methodKey = key;
    },
    setMedicalUUID(state, {payload}: PayloadAction<{uid?: string | null}>) {
      const {uid} = payload;
      state.medicalUUID = uid;
    },
    setAnswerOrder(state, {payload}: PayloadAction<{order: number}>) {
      const {order} = payload;
      state.answerOrder = order;
    },
    setBackAnswerOrder(state, {payload}: PayloadAction<{}>) {
      if (state.answerOrder > 0) {
        state.answerOrder--;
      }
    },
    setUserLocalPhotoURL(state, {payload}: PayloadAction<{url: string}>) {
      const {url} = payload;
      state.userLocalPhotoURL = url;
    },
    setQuestionnaireMeta(state, {payload}: PayloadAction<{meta: MDQMeta}>) {
      const {meta} = payload;
      state.questionnaireMeta = meta;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchQuestionnaires.fulfilled, (state, {payload}) => {});
    builder.addCase(addQuestionnaire.fulfilled, (state, {payload}) => {});
    builder.addCase(submitOrLogMdq.fulfilled, (state, {payload}) => {
      state.medicalUUID = payload.uid;
    });
    // builder.addCase(updateQuestionnaire.fulfilled, (state, {payload}) => {
    //   upsertOneMutably(questionnairesAdapter, state, payload.normalized.entities.questionnaires);
    // });
    // builder.addCase(deleteQuestionnaire.fulfilled, (state, {payload, meta}) => {
    //   const {arg} = meta;
    //   questionnairesAdapter.removeOne(state, arg?.id as EntityId);
    //   deleteSuccess(state, arg?.id);
    // });
  },
});

export const {
  setActiveQuestionnaire,
  setNextQuestionnaire,
  setBackQuestionnaire,
  setQuestionnaireAnswer,
  setMethodKey,
  setMedicalUUID,
  setBackAnswerOrder,
  setUserLocalPhotoURL,
  setQuestionnaireMeta,
} = questionnaires.actions;

export default questionnaires.reducer;

export const {
  selectById: selectQuestionnaireById,
  selectIds: selectQuestionnairesIds,
  selectEntities: selectQuestionnairesEntities,
  selectAll: selectAllQuestionnaires,
  selectTotal: selectTotalQuestionnaires,
} = questionnairesAdapter.getSelectors((state: RootState) => state.questionnaires);

export const [getQuestionnaires] = createSelectAllBySource('questionnaires', selectQuestionnairesEntities, [
  SOURCES_QUESTIONNAIRE.QUESTIONNAIRES_PAGE,
]);
